import {Component, Inject, OnInit, VERSION, ViewChild} from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import {ConfirmDialogComponent} from "../../confirm-dialog/confirm-dialog.component";
import {LanguagePipe} from "../../../pipe/language.pipe";
import {UserService} from "../../../service-data/user.service";
import {Product} from "../../../../model/Product";
import {FacilityInventoryProduct} from "../../../../model/FacilityInventoryProduct";
import {FacilityInventoryProductService} from "../../../service-data/facility-inventory-product.service";
import {Organization} from "../../../../model/Organization";
import {OrganizationService} from "../../../service-data/organization.service";
import {ProductService} from "../../../service-data/product.service";
import {MatTableDataSource} from "@angular/material/table";
import {StartupService} from "../../../service-ui/startup.service";
import {BookService} from "../../../service-data/book.service";
import {Facility} from "../../../../model/Facility";
import {FacilityInventoryProductEventService} from "../../../service-data/facility-inventory-product-event.service";

@Component({
  selector: 'app-add-facility-inventory-product',
  templateUrl: './add-facility-inventory-product.component.html',
  styleUrls: ['./add-facility-inventory-product.component.css']
})
export class AddFacilityInventoryProductComponent implements OnInit {
  public products: Product[];
  public organizations: Organization[];
  public breakpoint: number; // Breakpoint observer code
  public printnode_ID: number = 0;
  public orgaizationId: number = 0;
  public location: string = '';
  public shelf: string = '';
  public quantityOnHand: number = 0;
  public quantityCommited: number = 0;
  public state: string = 'PREPARING';
  public inventoryManagementType: string = 'LEVEL';
  public product: Product;
  public facilityInventoryProduct: FacilityInventoryProduct;
  public sequence: string = '1';
  public reorderQuantity: number = 20;
  public reorderLevel: number = 10;
  public caseQuantity: number = 0;
  public bookId: number;
  public initialQuantity: number = 0;
  /** POST in flight - guards against a double submit. */
  public saving: boolean = false;
  /** The POST came back without a row; the dialog stays open. */
  public addFailed: boolean = false;

  public errorMessage = '';
  public message = '';

  public addForm: FormGroup;
  wasFormChanged = false;

  /** When opened from a manufacturing-bucket order item, the caller can
   *  supply a pre-selected book and target facility so the operator
   *  doesn't have to look them up. */
  private prefilledBookId: number | undefined;

  constructor(private formBuilder: FormBuilder, public dialog: MatDialog, public organizationService: OrganizationService, public productService: ProductService, public bookService: BookService, public facilityInventoryProductService: FacilityInventoryProductService, public facilityInventoryProductEventService: FacilityInventoryProductEventService, public startupService: StartupService, @Inject(MAT_DIALOG_DATA) data, private dialogRef: MatDialogRef<AddFacilityInventoryProductComponent>) {
    if ( data.organizations ) {
      this.organizations = data.organizations;
    }
    // Optional pre-fill (e.g. opened from a PrintNodeOrderItem in a
    // manufacturing bucket).
    if ( data.bookId ) {
      this.bookId = Number(data.bookId);
      this.prefilledBookId = this.bookId;
    }
    if ( data.printnode_ID ) {
      this.printnode_ID = Number(data.printnode_ID);
    }
  }

  public ngOnInit(): void {
    this.addForm = this.formBuilder.group({
      // printnode_ID is a select with values '1' / '7'; require a
      // non-zero pick so the form is invalid until the user makes
      // a real choice (or the role-based default below fills it in).
      printnode_ID: [this.printnode_ID, [Validators.required, Validators.min(1)]],
      inventoryManagementType: [this.inventoryManagementType, [Validators.required]],
      // product: [this.product, [Validators.required]],
      // organizationId: [this.orgaizationId, []],
      sequence: [this.sequence, [Validators.required]],
      reorderQuantity: [this.reorderQuantity, [Validators.required, Validators.min(0)]],
      reorderLevel: [this.reorderLevel, [Validators.required, Validators.min(0)]],
      // Location & shelf are free-text and required.
      location: [this.location, [Validators.required, Validators.minLength(1)]],
      shelf: [this.shelf, [Validators.required, Validators.minLength(1)]],
      caseQuantity: [this.caseQuantity, [Validators.required, Validators.min(0)]],
      // bookId mirrors the search button's gate (>= 100).
      bookId: [this.bookId, [Validators.required, Validators.min(100)]],
      initialQuantity: [this.initialQuantity, [Validators.required, Validators.min(0)]]
    });

    if ( this.startupService.selectedRole === 'facility' ) {
      this.printnode_ID = Number(this.startupService.selectedRoleId);
      // Push the role-driven default into the form control so the
      // validator sees it.
      this.addForm.controls.printnode_ID.setValue(this.printnode_ID);
    }
    this.breakpoint = window.innerWidth <= 300 ? 1 : 2; // Breakpoint observer code
    if ( this.startupService.getRole() === 'organization') {
      this.orgaizationId = Number(this.startupService.getRoleId());
    } else {
      this.orgaizationId = this.startupService.getCurrentOrganization().ID;
    }
    // If the caller pre-filled a bookId, auto-run the product lookup so
    // the operator can hit "Add" immediately when called from a
    // PrintNodeOrderItem context.
    if ( this.prefilledBookId ) {
      this.lookupProductFromBookId();
    }
  }

  public lookupProductFromBookId() {
    let facilityID = this.addForm.controls.printnode_ID.value;
    if ( this.startupService.selectedRole === 'facility') {
      facilityID = Number(this.startupService.selectedRoleId);
    }
    const bookId = this.addForm.controls.bookId.value;
    this.message = 'Looking for Product for Book ID ' + bookId;
    this.errorMessage = '';
    this.product = undefined;
    let facilityInventoryProduct;
    for (const fip of this.facilityInventoryProductService.objects) {
      if ( fip.product && fip.product.book_ID === bookId && fip.printnode_ID === facilityID ) {
        facilityInventoryProduct = fip;
      }
    }
    // If no existing FacilityInventoryProduct exists for the bookId and facility then continue attempting to add
    if (!facilityInventoryProduct) {
      this.facilityInventoryProduct = undefined;
      let product;
      for (const p of this.productService.objects) {
        if (p.book_ID === bookId) {
          product = p;
        }
      }
      if ( product ) {
        this.product = product;
        this.message = '';
      } else {
        this.productService.getFilteredItems({ eq: { 'product.book_ID': bookId } }, true, 10).subscribe( products => {
          if ( products && products.length > 0 ) {
            this.product = products[0];
            for (const p of products ) {
              // Use a product that is not archived and is not a proof if one exists
              if ( p.archived === 'FALSE' ) {
                this.product = p;
              }
            }
            this.message = '';
          } else {
            this.message = '';
            this.errorMessage = 'Could not find a product for this bookId';
          }
        });
      }
    } else {
      this.facilityInventoryProduct = facilityInventoryProduct;
      this.message = '';
      this.errorMessage = 'An Inventory item already exists for this book ID and can not be added.';
    }
  }

  public onAdd(): void {
    // Look to see if there is already a FacilityInventoryProduct
    const objectToAdd = new FacilityInventoryProduct(undefined);
    objectToAdd.printnode_ID = this.addForm.controls.printnode_ID.value;
    if ( this.startupService.selectedRole === 'facility') {
      objectToAdd.printnode_ID = Number(this.startupService.selectedRoleId);
    }
    if ( this.product && !this.facilityInventoryProduct ) {
      objectToAdd.location = this.addForm.controls.location.value;
      objectToAdd.shelf = this.addForm.controls.shelf.value;
      objectToAdd.inventoryManagementType = this.addForm.controls.inventoryManagementType.value;
      objectToAdd.product_ID = this.product.ID;
      objectToAdd.productIds = '' + this.product.ID;
      objectToAdd.title = this.product.title;
      objectToAdd.quantityCommited = 0;
      objectToAdd.state = 'ACTIVE';
      objectToAdd.lastUpdate = new Date();
      objectToAdd.sequence = this.addForm.controls.sequence.value;
      objectToAdd.reorderQuantity = this.addForm.controls.reorderQuantity.value;
      objectToAdd.reorderLevel = this.addForm.controls.reorderLevel.value;
      objectToAdd.caseQuantity = this.addForm.controls.caseQuantity.value;
      objectToAdd.quantityOnHand = this.addForm.controls.initialQuantity.value;

      this.saving = true;
      this.addFailed = false;
      this.facilityInventoryProductService.addItem(objectToAdd).subscribe( addedFacilityInventoryProduct => {
        this.saving = false;
        // AbstractLiveDataService.handleError swallows a failed POST into
        // `of(undefined)`, so a rejection arrives here as a successful
        // emission of nothing rather than on the error callback.
        if ( !addedFacilityInventoryProduct || !addedFacilityInventoryProduct.ID ) {
          this.addFailed = true;
          return;
        }
        console.log(`Added FacilityInventoryProduct productId: ${addedFacilityInventoryProduct.product_ID}`);
        // No manual push onto `product.facilityInventoryProducts` here.
        // addItem -> addUpdateLocalItem -> linkObject already raises the
        // attach request that puts the SERVICE'S instance on that array,
        // so wrapping the response in a second `new FacilityInventoryProduct`
        // and pushing it added a detached duplicate that no notifier would
        // ever update. It also threw outright when the Product had no
        // `facilityInventoryProducts` array yet, which left the dialog open
        // on a row that had in fact been created.
        // if ( addedFacilityInventoryProduct.facilityInventoryProductEvents ) {
        //   if ( addedFacilityInventoryProduct.facilityInventoryProductEvents ) {
        //     for ( const facilityInventoryProductEvent of addedFacilityInventoryProduct.facilityInventoryProductEvents ) {
        //       if ( facilityInventoryProductEvent.source === 'INITIAL') {
        //         facilityInventoryProductEvent.quantity = Number(this.addForm.controls.initialQuantity);
        //         this.facilityInventoryProductEventService.updateFacilityInventoryProductEvent(facilityInventoryProductEvent).subscribe( changedFacilityInventoryProductEvent => {
        //           console.log(`Set the quantity of FacilityInventoryProduct productId: ${addedFacilityInventoryProduct.product_ID}`);
        //         });
        //       }
        //     }
        //   }
        // }
        this.markAsDirty(this.addForm);
        // Close only once the row exists. This used to run synchronously
        // alongside the request, so every `afterClosed()` handler - which
        // is how the caller learns to re-read its inventory - ran before
        // the server had answered and therefore saw no new row.
        this.dialogRef.close(addedFacilityInventoryProduct);
      });
    } else {
      this.errorMessage = 'Could not find a product for this bookId';
    }
  }

  public organizationSet() {
    const filter = { eq: {
        'product.org_ID': this.orgaizationId
      }
    };
    this.productService.getFilteredItems(filter)
      .subscribe(products => {
        this.products = products;
      });
  }

  public productSet() {
  }

  // tslint:disable-next-line:no-any
  public onResize(event: any): void {
    this.breakpoint = event.target.innerWidth <= 600 ? 1 : 2;
  }

  private markAsDirty(group: FormGroup): void {
    group.markAsDirty();
    // tslint:disable-next-line:forin
    for (const i in group.controls) {
      group.controls[i].markAsDirty();
    }
  }

  formChanged() {
    this.wasFormChanged = true;
  }

  /**
   * Human-readable list of fields blocking the Add button. Used as a
   * tooltip / aria-label so the operator can tell at a glance why
   * "Add Inventory Product" is greyed out.
   */
  public getAddButtonBlockers(): string {
    if ( !this.addForm ) { return ''; }
    const blockers: string[] = [];
    if ( !this.product ) { blockers.push('Lookup Product From BookId'); }
    const c = this.addForm.controls;
    if ( c.printnode_ID && c.printnode_ID.invalid ) { blockers.push('Facility'); }
    if ( c.bookId && c.bookId.invalid ) { blockers.push('BookId'); }
    if ( c.location && c.location.invalid ) { blockers.push('Location'); }
    if ( c.shelf && c.shelf.invalid ) { blockers.push('Shelf'); }
    if ( c.inventoryManagementType && c.inventoryManagementType.invalid ) { blockers.push('Inventory Management Type'); }
    if ( c.reorderQuantity && c.reorderQuantity.invalid ) { blockers.push('Reorder Quantity'); }
    if ( c.reorderLevel && c.reorderLevel.invalid ) { blockers.push('Reorder Level'); }
    if ( c.caseQuantity && c.caseQuantity.invalid ) { blockers.push('Case Quantity'); }
    if ( c.initialQuantity && c.initialQuantity.invalid ) { blockers.push('Initial Quantity'); }
    if ( c.sequence && c.sequence.invalid ) { blockers.push('sequence'); }
    return blockers.join(', ');
  }

}
